import util from '@core/services/util';
import ENUMS from '@core/classes/Enums';
import _ from 'underscore';

export default class RichTableHeader {
	constructor(init) {
		/** The corresponding property name */
		this.name = '';

		/** The text display for the column */
		this.display = '';

		this.columnOrder = 100;

		/**  *CSS class for header + data cells  */
		this.cssClass = '';

		/** CSS class for data cells only */
		this.cellCssClass = '';

		/** Font Awesome icon class name (replaces header display with icon) */
		this.icon = null;

		/** List of buttons/links to insert in either the data cells or the header (see documentation below) */
		this.actions = null;

		/** Name of slot to use for column's data cells */
		this.slot = null;

		this.component = null;

		/** If using component, changes what data the component is v-bind.sync to (see documentation below) */
		this.nonObject = false;

		/** List of headers that get stacked together into one column */
		this.subHeaders = null;

		/** Function whose return value gets displayed at the bottom of the column (passed no parameters) */
		this.footer = null;

		/** Only used for serverside data; whether or not to append '.keyword' to ElasticSearch query */
		this.useKeyword = false;

		/** An additional filtering function to apply when retrieving the row value */
		this.filter = null;

		this.value = null;

		/** Value function to use when exporting the table */
		this.printValue = null;

		this.sortable = false;

		/** Value function to use when sorting the column */
		this.sortValue = null;

		/** Sort direction (whether or no is ascending) */
		this.asc = null;

		this.searchable = false;

		this.rangeFilter = null;
		this.autoFilter = null;

		this.isVisible = true;
		this.isOpen = false;
		this.isSubHeader = false;
		this.dontAllowExport = false; // When true, this header will not show up on the export list when trying to export RichTable to file.
		this.anythingChanged = false;

		if (init) {
			this.name = 'name' in init ? init.name : 'value';
			this.display = init.display;
			this.columnOrder = init.columnOrder ?? 100;
			this.cssClass = (init.cssClass ?? '') + (init.autoFilter || init.sortable || init.rangeFilter || init.subHeaders ? ' clickable' : '');
			this.cellCssClass = init.cellCssClass ?? '';
			this.icon = init.icon;
			this.actions = init.actions;
			this.slot = init.slot;
			this.component = init.component;
			this.nonObject = init.nonObject;
			this.subHeaders = init.subHeaders;
			this.footer = init.footer;
			this.useKeyword = init.useKeyword ?? false;
			this.isVisible = init.isVisible ?? true;
			this.dontAllowExport = init.dontAllowExport ?? false;
			this.isSubHeader = init.isSubHeader ?? false;

			this.sortable = 'sortable' in init ? init.sortable : false;
			this.sortValue = init.sortValue;
			this.asc = init.asc;

			this.searchable = init.searchable ?? false;

			this.rangeFilter =
				typeof init.rangeFilter === 'boolean' && init.rangeFilter
					? {
							min: null,
							max: null,
							rangeFilterType: ENUMS.RICHTABLE_RANGE_FILTER_TYPES.NUMERIC,
						}
					: init.rangeFilter;

			if (!util.isNull(init.autoFilter) && typeof init.autoFilter === 'boolean' && init.autoFilter) {
				this.autoFilter = {
					values: null,
					searchText: null,
					deselectedValues: [],
					hideSearch: false,
					showAsFlexbox: false,
					gridDisplayColumns: null,
				};
			} else if (!util.isNull(init.autoFilter) && typeof init.autoFilter === 'object') {
				this.autoFilter = {
					values: init.autoFilter.values.map(v => ({
						display: v.display,
						value: v.value,
						isSelected: v.isSelected,
					})),
					searchText: null,
					deselectedValues: init.autoFilter.deselectedValues,
					hideSearch: init.autoFilter.hideSearch ?? false,
					showAsFlexbox: init.autoFilter.showAsFlexbox ?? false, // Boolean; Autofilter values displayed as a flexbox
					gridDisplayColumns: init.autoFilter.gridDisplayColumns ?? null, // Number; Autofilter values displayed as a grid with gridDisplayColumns columns
				};
			}

			//A subheader group doesn't have its own value
			if (!init.subHeaders) {
				this.filter = init.filter;

				this.value = !util.isNull(init.value) && util.isFunction(init.value) ? init.value : new Function('row', `return row.${init.rowValue ?? this.name}`);

				this.printValue = !util.isNull(init.printValue) && util.isFunction(init.printValue) ? init.printValue : this.value;
			}
		}
	}

	initAutoFilters(initialData) {
		if (!this.autoFilter) return;

		//Autofilter values provided
		if (typeof this.autoFilter === 'object' && this.autoFilter.values) {
			this.autoFilter.deselectedValues = this.autoFilter.values.map(v => v.value);
			return;
		}

		//Generate autofilter values
		let allRowValues = [];
		if (initialData && initialData.length > 0) {
			let sampleValue = this.rowValue(initialData[0]);

			if (sampleValue && Array.isArray(sampleValue)) {
				if (sampleValue.length > 0) initialData.forEach(d => allRowValues.push(...this.rowValue(d)));
			} else {
				allRowValues = initialData.map(d => this.rowValue(d));
			}
		}

		let uniqueRowValues = _.uniq(allRowValues);
		let uniqueRowValuesWithMetaData = uniqueRowValues.map(v => {
			return {
				isSelected: false,
				display: v?.toString() ?? '',
				value: v?.toString() ?? '',
			};
		});

		let sortedValues = _.sortBy(uniqueRowValuesWithMetaData, s => s.display);
		this.autoFilter.values = sortedValues;
		this.autoFilter.deselectedValues = sortedValues.map(v => v.display);
	}

	rowValue(row) {
		return this.filter ? this.filter(this.value(row)) : this.value(row);
	}

	getCellCssClass(row) {
		if (util.isNull(this.cssClass) && util.isNull(this.cellCssClass)) return '';

		let secondClass = util.isFunction(this.cellCssClass) ? this.cellCssClass(row) : this.cellCssClass;
		let combinedCssClass = `${this.cssClass ?? ''} ${secondClass ?? ''}`;
		return combinedCssClass.trim();
	}

	getNumSelectedAutoFilters() {
		if (!this.autoFilter) return 0;
		return this.autoFilter.values.filter(v => v.isSelected).length;
	}

	autoFilterMenuAvailable() {
		if (!this.subHeaders || this.subHeaders.length < 1) return this.autoFilter || this.rangeFilter;

		return this.subHeaders.some(sh => sh.autoFilter || sh.sortable || sh.rangeFilter);
	}

	hasSelectedAutoFilter() {
		if (this.subHeaders) return this.subHeaders.some(sh => sh.autoFilter?.values?.some(v => v.isSelected));

		return !!this.autoFilter?.values?.some(v => v.isSelected);
	}

	hasActiveRangeFilter() {
		if (this.subHeaders) {
			return this.subHeaders.some(sh => {
				return sh.rangeFilter && (sh.rangeFilter.max !== null || sh.rangeFilter.min !== null);
			});
		}

		if (!this.rangeFilter) return false;
		return this.rangeFilter.max !== null || this.rangeFilter.min !== null;
	}

	hasFlexDisplay() {
		if (!this.autoFilter) return false;
		return this.autoFilter.showAsFlexbox && !this.autoFilter.gridDisplayColumns;
	}

	hasGridDisplay() {
		if (!this.autoFilter) return false;
		return this.autoFilter.gridDisplayColumns && this.autoFilter.gridDisplayColumns > 0 && !this.autoFilter.showAsFlexbox;
	}

	getGridColumnCssVar() {
		if (!this.autoFilter?.gridDisplayColumns) return '';
		return `--grid-columns: ${this.autoFilter.gridDisplayColumns}`;
	}
}

/* ACTIONS
 * Format: [{type: '', name: '', text: '', cssClass: '', icon: '', header: true/false, action: () => {}, disabled: true/false }, ...]
 *      Type: button | link | route (data cell only)
 *      Header: if true, action will be inserted into the header (below the header's title) instead of the data cell
 *      Action: function that gets called when the button/link is clicked
 *          In Header: no parameters
 *          In Data Cell: (row, rowIndex, tableData) => {}
 */

/* NONOBJECT
 * True: component is v-bind.sync to { data: { nameValue: row._ref[header.name], value: header.value, row: row } }
 * False: component is v-bind.sync to { ...row[header.name] }
 */
